import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LogOut, Bell, Sun, Moon, BookOpen, Compass, AlertTriangle, ChevronRight, Clock } from 'lucide-react';
import { useUserStore } from '@/store/userStore';
import { useThemeStore } from '@/store/themeStore';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useTour } from '@/components/common/OnboardingTour';
import { useNMNotifications } from '@/hooks/useNMNotifications';

const PAGE_TITLES = {
  '/': 'Dashboard',
  '/liderazgo': 'Liderazgo',
  '/ops': 'OPS - Observaciones de Seguridad',
  '/near-misses': 'Near Misses',
  '/mapa': 'Mapa Operacional',
  '/admin': 'Administración',
  '/ayuda': 'Manual de Usuario',
  '/siniestralidad': 'Siniestralidad',
  '/correlacion': 'Análisis de Correlación',
};

const ROLE_LABELS = {
  superadmin: { label: 'Super Admin', color: '#00C853' },
  admin: { label: 'Admin', color: '#4A9EFF' },
  gerente: { label: 'Gerente', color: '#FFD600' },
  responsable: { label: 'Responsable', color: '#FF6B35' },
  ops: { label: 'OPS', color: '#4A9EFF' },
  viewer: { label: 'Viewer', color: '#546E7A' },
  global_viewer: { label: 'Lectura Global', color: '#26C6DA' },
};

const GRAVEDAD_COLORS = {
  verde: '#00A847', Verde: '#00A847',
  amarillo: '#D4A300', Amarillo: '#D4A300',
  naranja: '#FF6B35', Naranja: '#FF6B35',
  rojo: '#E53935', Rojo: '#E53935',
};

export default function TopBar() {
  const { user, logout, token } = useUserStore();
  const { theme, toggleTheme } = useThemeStore();
  const location = useLocation();
  const navigate = useNavigate();
  const title = PAGE_TITLES[location.pathname] || 'Dashboard';
  const roleInfo = ROLE_LABELS[user?.role] || ROLE_LABELS.viewer;
  const isDark = theme === 'dark';
  const tour = useTour();
  const [notifOpen, setNotifOpen] = useState(false);

  const { unread, latestNM, markAsRead } = useNMNotifications(token);

  const handleNotifOpen = () => {
    setNotifOpen(v => !v);
  };

  const handleGoToNM = () => {
    markAsRead();
    setNotifOpen(false);
    navigate('/near-misses');
  };

  return (
    <header
      className="relative flex items-center justify-between px-4 lg:px-6 h-14 border-b flex-shrink-0"
      style={{ background: 'var(--vz-bg-card)', borderColor: 'var(--vz-border-subtle)', zIndex: 30 }}
    >
      <div className="flex items-center gap-3 min-w-0">
        <h1 className="font-montserrat font-semibold text-base truncate max-w-[160px] sm:max-w-none" style={{ color: 'var(--vz-text-primary)' }}>{title}</h1>
      </div>

      {/* Centered banner – hidden on very small screens */}
      <div className="hidden md:flex items-center gap-1.5 absolute left-1/2 -translate-x-1/2 pointer-events-none" data-testid="powered-by-banner">
        <span className="text-[11px] font-montserrat" style={{ color: 'var(--vz-text-muted)' }}>Powered by</span>
        <span className="text-[11px] font-montserrat font-semibold tracking-wide" style={{ color: 'var(--vz-green)' }}>Valoriza's Innovation Team</span>
      </div>

      <div className="flex items-center gap-1 sm:gap-2">
        {/* Theme toggle */}
        <Button variant="ghost" size="icon" aria-label={isDark ? 'Modo claro' : 'Modo oscuro'} onClick={toggleTheme} data-testid="topbar-theme-toggle">
          {isDark ? <Sun size={16} style={{ color: 'var(--vz-yellow)' }} /> : <Moon size={16} style={{ color: 'var(--vz-text-muted)' }} />}
        </Button>

        {/* Notification Bell */}
        <div className="relative">
          <Button
            variant="ghost" size="icon"
            aria-label={`Notificaciones${unread > 0 ? ` (${unread} nuevas)` : ''}`}
            onClick={handleNotifOpen}
            data-testid="topbar-notifications"
            className="relative"
          >
            <Bell size={16} style={{ color: unread > 0 ? '#FFD600' : 'var(--vz-text-secondary)' }} />
            {unread > 0 && (
              <span
                className="absolute top-0.5 right-0.5 min-w-[16px] h-4 rounded-full flex items-center justify-center text-[9px] font-bold leading-none px-0.5"
                style={{ background: '#FF5252', color: '#fff', zIndex: 10 }}
                data-testid="notif-badge"
              >
                {unread > 99 ? '99+' : unread}
              </span>
            )}
          </Button>

          {/* Notification panel */}
          {notifOpen && (
            <div
              className="absolute right-0 top-full mt-2 w-80 rounded-xl border shadow-2xl overflow-hidden z-50"
              style={{ background: 'var(--vz-bg-card)', borderColor: 'var(--vz-border-subtle)' }}
              data-testid="notif-panel"
            >
              <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'var(--vz-border-subtle)' }}>
                <div className="flex items-center gap-2">
                  <Bell size={14} style={{ color: '#FFD600' }} />
                  <span className="font-montserrat text-xs font-semibold" style={{ color: 'var(--vz-text-primary)' }}>Near Misses</span>
                </div>
                {unread > 0 && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full font-bold" style={{ background: 'rgba(255,82,82,0.15)', color: '#FF5252' }}>
                    {unread} nuevos
                  </span>
                )}
              </div>

              <div className="p-3">
                {unread === 0 ? (
                  <div className="text-center py-6">
                    <Bell size={24} className="mx-auto mb-2 opacity-20" style={{ color: 'var(--vz-text-muted)' }} />
                    <p className="text-xs" style={{ color: 'var(--vz-text-muted)' }}>Sin nuevos Near Misses</p>
                    <p className="text-[10px] mt-1" style={{ color: 'var(--vz-text-muted)' }}>Revisión automática cada 60 seg.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {latestNM && (
                      <div
                        className="p-3 rounded-lg border"
                        style={{
                          background: 'rgba(255,210,0,0.05)',
                          borderColor: `${GRAVEDAD_COLORS[latestNM.gravedad] || '#FFD600'}30`,
                        }}
                      >
                        <div className="flex items-start gap-2">
                          <AlertTriangle size={13} style={{ color: GRAVEDAD_COLORS[latestNM.gravedad] || '#FFD600', marginTop: 1, flexShrink: 0 }} />
                          <div className="min-w-0">
                            <p className="text-xs font-semibold truncate" style={{ color: 'var(--vz-text-primary)' }}>
                              {latestNM.centro_nombre || 'Centro sin nombre'}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold" style={{ background: `${GRAVEDAD_COLORS[latestNM.gravedad] || '#FFD600'}20`, color: GRAVEDAD_COLORS[latestNM.gravedad] || '#FFD600' }}>
                                {latestNM.gravedad ? latestNM.gravedad.charAt(0).toUpperCase() + latestNM.gravedad.slice(1) : 'Sin nivel'}
                              </span>
                              {latestNM.fecha && (
                                <span className="text-[10px] flex items-center gap-1" style={{ color: 'var(--vz-text-muted)' }}>
                                  <Clock size={9} /> {latestNM.fecha}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    {unread > 1 && (
                      <p className="text-[10px] text-center" style={{ color: 'var(--vz-text-muted)' }}>
                        y {unread - 1} más desde tu última visita
                      </p>
                    )}
                  </div>
                )}
              </div>

              <div className="border-t p-2" style={{ borderColor: 'var(--vz-border-subtle)' }}>
                <button
                  onClick={handleGoToNM}
                  className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs font-medium transition-colors hover:bg-white/5"
                  style={{ color: '#4A9EFF' }}
                  data-testid="notif-goto-nm"
                >
                  Ver todos los Near Misses
                  <ChevronRight size={13} />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="flex items-center gap-2 px-2" data-testid="topbar-user-menu">
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                style={{ background: roleInfo.color + '30', color: roleInfo.color }}
              >
                {(user?.display_name || user?.email || 'U').charAt(0).toUpperCase()}
              </div>
              <div className="hidden sm:block text-left">
                <p className="text-xs font-medium leading-tight truncate max-w-[100px]" style={{ color: 'var(--vz-text-primary)' }}>
                  {user?.display_name || user?.email?.split('@')[0] || '—'}
                </p>
                <p className="text-[10px]" style={{ color: 'var(--vz-text-muted)' }}>{roleInfo.label}</p>
              </div>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48" style={{ background: 'var(--vz-bg-card)', borderColor: 'var(--vz-border-subtle)' }}>
            <DropdownMenuLabel className="text-xs truncate" style={{ color: 'var(--vz-text-secondary)' }}>{user?.email}</DropdownMenuLabel>
            <DropdownMenuSeparator style={{ background: 'var(--vz-border-subtle)' }} />
            <DropdownMenuItem onClick={() => tour?.startTour()} data-testid="topbar-start-tour" className="cursor-pointer text-xs">
              <Compass size={13} className="mr-2" /> Tour guiado
            </DropdownMenuItem>
            <DropdownMenuItem asChild data-testid="topbar-download-manual" className="cursor-pointer text-xs">
              <a href="/Manual_de_Usuario.pdf" download="Manual_de_Usuario_Valoriza_2026.pdf">
                <BookOpen size={13} className="mr-2" /> Descargar Manual
              </a>
            </DropdownMenuItem>
            <DropdownMenuSeparator style={{ background: 'var(--vz-border-subtle)' }} />
            <DropdownMenuItem onClick={logout} data-testid="topbar-logout-button" className="text-red-400 hover:text-red-300 cursor-pointer text-xs">
              <LogOut size={13} className="mr-2" /> Cerrar sesión
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
